import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "react-draggable";
import { useParams } from "react-router-dom";
import actions from "../../recommendations/actions";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build";
import { RichEditorPopup } from "./formFields/RichEditorPopup";
import Portal from "./Portal";
import { CkeditorConfiguration } from "../../smartCharts/CkEditorConfig";
import ConfirmationModal from "./ConfirmationModal";
import { showAlert } from "../../../config/utils";

function AddIssueModal({
    onCancel,
    onSubmit,
    title = "",
    heading = "",
    isMemo = false,
    fieldHeading = "",
    isSubmitLoading = false,
    isDisabled = false,
    isCbreReview = false
}) {
    const [issueTitle, setIssueTitle] = useState(title || "");
    const [isErr, setIsErr] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();
    const { id, tab } = useParams();
    const { addQaQcIssueResponse } = useSelector(state => state.recommendationsReducer) || {};
    const [showRichTextPopup, setShowRichTextPopup] = useState({ value: "", label: "", key: "", onSaveData: () => {}, show: false });
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    useEffect(() => {
        if (addQaQcIssueResponse?.message) {
            showAlert(addQaQcIssueResponse?.errors?.title ? "The issue title already exists" : addQaQcIssueResponse?.message);
            dispatch(
                actions.getAllRecommendationReviews({
                    recommendation_id: id,
                    order: {
                        [`recommendation_reviews.created_at`]: "desc"
                    }
                })
            );
            dispatch(actions.getRecommendationById(id));
            if (addQaQcIssueResponse?.success) {
                onCancel();
            }
            setIsLoading(false);
        }
    }, [addQaQcIssueResponse]);
    const cancelCommentModal = () => {
        const defaultTitile = title ?? "";
        if (defaultTitile !== issueTitle) setShowCancelConfirmation(true);
        else onCancel();
    };
    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                cancelCommentModal();
            }
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [cancelCommentModal]);

    const handleSave = () => {
        if (!issueTitle?.trim() && !isCbreReview) return setIsErr(true);
        if (onSubmit) {
            onSubmit(issueTitle);
        } else {
            setIsLoading(true);
            dispatch(actions.createQaQcIssue({ recommendation_review: { recommendation_id: id, title: issueTitle } }));
        }
    };

    const handleChange = ({ target: { value } }) => {
        setIssueTitle(value);
        setIsErr(false);
    };
    const toggleShowRichTextPopup = value => setShowRichTextPopup(value);

    const renderRichTextPopup = () => {
        const { label, key, onSaveData, show, value } = showRichTextPopup;
        if (!show) return null;
        return (
            <Portal
                body={
                    <RichEditorPopup
                        value={value}
                        label={label}
                        key={key}
                        onCancel={() => toggleShowRichTextPopup({ ...showRichTextPopup, show: false })}
                        saveData={onSaveData}
                        outerClassName="comment_rich_text"
                    />
                }
                onCancel={() => toggleShowRichTextPopup({ ...showRichTextPopup, show: false })}
            />
        );
    };

    const renderConfirmationModal = () => {
        if (!showCancelConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={"Do you want to cancel and lose all changes?"}
                        type="cancel"
                        message={"This action cannot be reverted, are you sure that you need to cancel?"}
                        onNo={() => setShowCancelConfirmation(false)}
                        onYes={onCancel}
                    />
                }
                onCancel={() => setShowCancelConfirmation(false)}
            />
        );
    };

    return (
        <div className="modal modal-region qa-band-modl" id="modalId" tabIndex="-1" style={{ display: "block" }} role="dialog">
            <Draggable handle=".draggable" cancel=".close">
                <div className={`modal-dialog ${isMemo?"Qc-comment-modal":""}`} role="document">
                    <div className="modal-content">
                        <div className="modal-header draggable">
                            <h5 className="modal-title" id="exampleModalLabel">
                                <div className="txt-hed">{heading ? heading : onSubmit ? "Edit title" : "Add New QA/QC Issue"}</div>
                            </h5>
                            <button type="button" className="close close-icn-sec" onClick={cancelCommentModal}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="" />
                                </span>
                            </button>
                        </div>
                        <div className="modal-body region-otr">
                            <div className="col-md-12 more-info-content p-0">
                                <div className="form-group">
                                    <label className={`d-flex ${isMemo ? "justify-content-between align-items-center w-100 comment_memo" : ""}`}>
                                        {fieldHeading || "Issue title"}
                                        {isMemo ? (
                                            <button
                                                type="button"
                                                className="butn-expansion btn"
                                                data-for="input_field"
                                                data-tip="Expand Field"
                                                onClick={() => {
                                                    toggleShowRichTextPopup({
                                                        value: issueTitle ?? "",
                                                        label: "Comments",
                                                        key: "comments",
                                                        onSaveData: value => {
                                                            handleChange({ target: { value } });
                                                            toggleShowRichTextPopup({
                                                                ...showRichTextPopup,
                                                                value,
                                                                show: false
                                                            });
                                                        },
                                                        show: true
                                                    });
                                                }}
                                            >
                                                <img src="/img/expand1.svg" alt="" />
                                            </button>
                                        ) : null}
                                    </label>

                                    {isMemo ? (
                                        <div className="comment_note my-ckeditor-wrapper">
                                            <CKEditor
                                                editor={Editor}
                                                config={{ ...CkeditorConfiguration}}
                                                id="report-note-template"
                                                data={issueTitle || ""}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    handleChange({ target: { value: data } });
                                                }}
                                                onReady={editor => {
                                                    if (editor) {
                                                        editor.setData(issueTitle ?? "");
                                                    }
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <textarea
                                            placeholder="Notes"
                                            autoFocus
                                            className={`form-control ${isErr ? "error-border" : ""}`}
                                            value={issueTitle}
                                            onChange={handleChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="btn-sec">
                                <div className="text-right btnOtr edit-cmn-btn">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btnClr col-md-2 mr-1"
                                        data-dismiss="modal"
                                        onClick={cancelCommentModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        disabled={isLoading || isMemo ? issueTitle === title : false}
                                        type="button"
                                        className="btn btn-primary btnRgion col-md-2"
                                        onClick={handleSave}
                                    >
                                        {isLoading || isSubmitLoading ? (
                                            <div className="button-loader d-flex justify-content-center align-items-center">
                                                <div className="spinner-border text-white" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            "Save"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
            {renderRichTextPopup()}
            {renderConfirmationModal()}
        </div>
    );
}

export default AddIssueModal;
